import { gql, useQuery, NetworkStatus } from "@apollo/client";
import moment from "moment-timezone";

import { addAuthVars } from "@teamrota/authlib";

import useAuth from "~/src/auth/hooks/use-auth";

/**
 * Provide-schedule only: unassigned shifts with bookingCounts (one field per shift)
 */
export const GET_UNASSIGNED_SHIFTS_PROVIDE_SCHEDULE = addAuthVars(gql`
  query getUnassignedShiftsProvideSchedule(
    $startTime: Date!
    $endTime: Date!
    $roleIds: [ID]
    $homeVenueIds: [ID]
    $venueIds: [ID]
    $userIds: [ID]
    $partnerIds: [ID]
    $isShowCancelledShifts: Boolean
    $shiftTypes: [ShiftTypeEnumType]
    $shiftSources: [ShiftSourceEnumType]
    $createdAfter: Date
  ) {
    account {
      id
      schedule(
        startTime: $startTime
        endTime: $endTime
        roleIds: $roleIds
        homeVenueIds: $homeVenueIds
        venueIds: $venueIds
        userIds: $userIds
        partnerIds: $partnerIds
        isShowCancelledShifts: $isShowCancelledShifts
        shiftTypes: $shiftTypes
        shiftSources: $shiftSources
        createdAfter: $createdAfter
      ) {
        totalUnassignedBookingsCount
        unassignedShifts {
          date
          shifts {
            id
            identifier
            type
            startTime
            numberRequested
            endTime
            finalisedAt
            openedAt
            isDraft
            isOnCallTimesEnabled
            isPredicted
            isShiftRebook
            name
            sourceAccountId
            targetAccountId
            shiftGroupId
            cancelledAt
            hasCancelledBookings
            stream {
              isAutomate
              sources {
                id
                numberRequested
                numberFilled
                gender
                url
              }
              account {
                id
                source
                sourceName
                name
                url
              }
            }
            sourceAccount {
              accountName
            }
            venue {
              id
              address
              name
            }
            subvenue {
              id
              name
            }
            ... on shift {
              payRate
              roleRate {
                id
                roleName
                roleId
                isReservedBookingsEnabled

                ... on roleRate {
                  payRate
                }
              }
            }
            bookingCounts {
              accepted
              cancelledWithoutReplacement
              reserved
            }
          }
        }
      }
    }
  }
`);

function normalizeUnassignedShifts(unassignedShifts) {
  if (!unassignedShifts || !Array.isArray(unassignedShifts))
    return unassignedShifts;
  return unassignedShifts.map(day => ({
    ...day,
    shifts: (day.shifts || []).map(shift => {
      const counts = shift.bookingCounts || {};
      return {
        ...shift,
        bookings: { totalResults: counts.accepted ?? 0 },
        reservedBookings: { totalResults: counts.reserved ?? 0 },
        cancelledBookings: {
          totalResults: counts.cancelledWithoutReplacement ?? 0
        }
      };
    })
  }));
}

export const useUnassignedShiftsProvideSchedule = ({
  startTime,
  endTime,
  createdAfter,
  homeVenueIds,
  roleIds,
  partnerIds,
  venueIds,
  userIds,
  isShowCancelledShifts,
  shiftTypes = [],
  shiftSources = [],
  skip
}) => {
  const auth = useAuth();

  const { data, refetch, loading, networkStatus } = useQuery(
    GET_UNASSIGNED_SHIFTS_PROVIDE_SCHEDULE,
    {
      variables: auth.addVals(GET_UNASSIGNED_SHIFTS_PROVIDE_SCHEDULE, {
        startTime: moment(startTime)
          .local()
          .format(),
        endTime: moment(endTime)
          .endOf("day")
          .local()
          .format(),
        createdAfter:
          createdAfter !== null
            ? moment(createdAfter)
                .local()
                .format()
            : null,
        homeVenueIds,
        roleIds,
        partnerIds,
        venueIds,
        userIds,
        isShowCancelledShifts,
        shiftTypes,
        shiftSources
      }),
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      skip,
      onError: error => {
        throw error;
      }
    }
  );

  const scheduleFromUnassigned = data?.account?.schedule;
  const rawUnassignedShifts = scheduleFromUnassigned?.unassignedShifts;

  return {
    unassignedShiftsLoading: loading || networkStatus === NetworkStatus.refetch,
    unassignedShiftsData: normalizeUnassignedShifts(rawUnassignedShifts),
    totalUnassignedBookingsCount:
      scheduleFromUnassigned?.totalUnassignedBookingsCount || 0,
    refetchUnassignedShifts: refetch
  };
};
